<template>
  <v-app class="as-page">
    <component :is="$route.meta.layout || 'div'">
      <router-view/>
    </component>

    <!-- Cookie choice: no tracking script runs before it ([B33]). -->
    <consent-banner />
    
  </v-app>
</template>

<script>
import { ConsentChoice, consentState, startTracking } from '@/domains/consent/trackingConsent'
import locales from '@/locales/locales'
import { absoluteUrl, alternatePaths, canonicalPath } from '@/js/utils/canonicalUrl'
import { isMainPageIndexed, ROBOTS_NOINDEX } from '@/js/utils/searchIndexing'

/** Canonical and hreflang URLs point at the configured site, the same base the Pages Function uses. */
const SITE_URL = process.env.VUE_APP_URL

export default {
  components: {
    // Loaded lazily on purpose: the banner imports urlUtil, which pulls in the store and the router;
    // imported here eagerly it ran before the router finished and left MainLayout without urlUtil.
    ConsentBanner: () => import(/* webpackChunkName: "consent" */ '@/domains/consent/components/ConsentBanner.vue')
  },

  metaInfo () {
    const path = this.$route.path
    // Legal pages stay out of search (NON_INDEXED_MAIN_PATHS): robots noindex, and no hreflang,
    // which only makes sense between language versions Google should index.
    const isIndexed = isMainPageIndexed(path)
    return {
      titleTemplate: this.$t('seo.title-template'),
      meta: [
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'url', content: window.location.href || '' },
        { property: 'og:url', content: window.location.href || '' },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: 'ASport' },
        { property: 'og:locale', content: locales.getLocale().locale },
        { property: 'fb:app_id', content: '1174440243160965' },
        ...(isIndexed ? [] : [{ name: 'robots', content: ROBOTS_NOINDEX }])
      ],
      link: [
        { rel: 'canonical', href: absoluteUrl(SITE_URL, canonicalPath(path)) },
        ...(isIndexed ? alternatePaths(path) : []).map(alternate => ({
          rel: 'alternate', hreflang: alternate.hreflang, href: absoluteUrl(SITE_URL, alternate.path)
        }))
      ],
      htmlAttrs: {
        lang: this.$i18n.locale,
        class: this.webpSupported ? 'webp' : 'no-webp'
      }
    }
  },
  data () {
    return {
      webpSupported: false
    }
  },
  created: function(){
    this.webpSupportCheck()
    // A visitor who already said yes: start the tracking tools at once. Otherwise they wait for the banner.
    if (consentState.choice === ConsentChoice.ACCEPTED) {
      startTracking(this.$mixpanel)
    }
  },
  methods:{
    webpSupportCheck () {
      (async () => {
        // If browser doesn't have createImageBitmap, we can't use webp.
        if (!self.createImageBitmap) {
          this.webpSupported = false
          return
        }

        // Base64 representation of a white point image
        const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoCAAEAAQAcJaQAA3AA/v3AgAA='
        // Retrieve the Image in Blob Format
        const blob = await fetch(webpData).then(r => r.blob())
        // If the createImageBitmap method succeeds, return true, otherwise false
        this.webpSupported = await createImageBitmap(blob).then(() => true, () => false)
      })()
    },
  }
}
</script>

